// ABOUTME: Bootstraps Module Federation runtime from the host registry
// ABOUTME: Uses registerRemotes (the vite build plugin already inits the host)

import type { ModuleExports, ModuleRegistryEntry } from '@alpha-finder/module-contract';
import { loadRemote, registerRemotes } from '@module-federation/runtime';
import { currentToken } from './auth-client';

export function remoteNameForId(id: string): string {
  return `module_${id.toLowerCase().replace(/-/g, '_')}`;
}

let bootstrapPromise: Promise<ModuleRegistryEntry[]> | null = null;

/** Fetches the module registry with the blab-auth token, which the gateway verifies; none is sent when the token is empty. */
export async function fetchRegistry(
  getToken: () => Promise<string> = currentToken
): Promise<ModuleRegistryEntry[]> {
  try {
    const token = await getToken();
    const res = await fetch('/api/modules/registry', {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (res.ok) {
      const body = (await res.json()) as { data: ModuleRegistryEntry[] };
      return body.data;
    }
  } catch {
    // Not signed in or a network error — treat as an empty registry; modules surface their own load errors.
  }
  return [];
}

async function doBootstrap(): Promise<ModuleRegistryEntry[]> {
  const entries = await fetchRegistry();
  if (entries.length > 0) {
    // The @module-federation/vite plugin already init()s the host, so we only
    // register the runtime-discovered remotes onto it (NOT init(), which targets a
    // separate instance loadRemote can't see — RUNTIME-004). alias = the module id
    // so loadRemote(`${id}/Module`) resolves.
    registerRemotes(
      entries.map((m) => ({
        name: remoteNameForId(m.id),
        alias: m.id,
        entry: m.remoteEntry,
      })),
      { force: true }
    );
  }
  return entries;
}

export function bootstrapModules(): Promise<ModuleRegistryEntry[]> {
  if (!bootstrapPromise) bootstrapPromise = doBootstrap();
  return bootstrapPromise;
}

export async function loadModule(id: string): Promise<ModuleExports> {
  const entries = await bootstrapModules();
  // The app-start bootstrap may have run before the bearer was available (empty
  // registry). If the requested module isn't registered yet, re-fetch once with
  // the now-present session before giving up.
  if (!entries.some((m) => m.id === id)) {
    bootstrapPromise = null;
    await bootstrapModules();
  }
  const exports = await loadRemote<{ default: ModuleExports }>(`${id}/Module`);
  if (!exports?.default) throw new Error(`Module ${id} did not default-export ModuleExports`);
  return exports.default;
}
